import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, MapPin, Download, Grid3X3, Grid2X2, ChevronLeft, ChevronRight, X } from "lucide-react";
import { api, resolveUrl } from "../lib/api";
import { toast } from "sonner";

const PAGE_SIZES = { small: 24, large: 12 };

export default function EventPhotos() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("large"); // 'small' | 'large'
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState(null); // photo object or null

  useEffect(() => {
    let live = true;
    api.get(`/events/${id}/photos`)
      .then(({ data }) => {
        if (!live) return;
        setEvent(data.event);
        setPhotos(data.photos);
      })
      .catch((err) => {
        if (!live) return;
        setError(err.response?.status === 404
          ? "This photo gallery isn't available yet."
          : "Could not load gallery.");
      })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [id]);

  const perPage = PAGE_SIZES[view];
  const totalPages = Math.max(1, Math.ceil(photos.length / perPage));
  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return photos.slice(start, start + perPage);
  }, [photos, page, perPage]);

  useEffect(() => { setPage(1); }, [view]);

  const downloadAll = () => {
    toast.info("Preparing your ZIP — the download will begin shortly.");
    window.location.href = `${api.defaults.baseURL}/events/${id}/photos.zip`;
  };

  const openLightbox = (photo, idx) => setLightbox({ ...photo, idx: (page - 1) * perPage + idx });
  const closeLightbox = () => setLightbox(null);
  const stepLightbox = (delta) => {
    if (!lightbox) return;
    const next = Math.max(0, Math.min(photos.length - 1, lightbox.idx + delta));
    const targetPage = Math.floor(next / perPage) + 1;
    if (targetPage !== page) setPage(targetPage);
    setLightbox({ ...photos[next], idx: next });
  };

  if (loading) {
    return <div className="py-32 text-center text-gray-500" data-testid="gallery-loading">Loading gallery…</div>;
  }
  if (error) {
    return (
      <div className="py-32 text-center max-w-md mx-auto px-4" data-testid="gallery-error">
        <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-3">Not Available</div>
        <h1 className="font-display text-3xl text-[#1F3864] mb-3">{error}</h1>
        <Link to="/events" className="text-[#C00000] font-semibold underline">Back to Events</Link>
      </div>
    );
  }

  return (
    <div data-testid="event-photos-page">
      {/* Hero */}
      <section className="bg-[#0B1526] text-white py-16 sm:py-24 relative overflow-hidden">
        {event?.image_url && (
          <img src={resolveUrl(event.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        )}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/events" className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4 inline-block hover:text-white transition-colors">← Events</Link>
          <h1 className="font-display text-4xl sm:text-6xl leading-[0.95] max-w-3xl" data-testid="gallery-event-title">{event?.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/70">
            {event?.event_date && (
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />
                {new Date(event.event_date).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            )}
            {event?.venue && <span className="flex items-center gap-2"><MapPin className="w-4 h-4" />{event.venue}</span>}
            <span className="text-white/90" data-testid="gallery-count">{photos.length} photo{photos.length === 1 ? "" : "s"}</span>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="sticky top-20 bg-white border-b border-gray-200 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 border border-gray-300">
            <button
              onClick={() => setView("large")}
              className={`px-3 py-2 flex items-center gap-2 text-xs tracking-[0.15em] uppercase ${view === "large" ? "bg-[#1F3864] text-white" : "text-gray-700 hover:bg-gray-50"}`}
              data-testid="view-large"
              aria-label="Large tiles"
            ><Grid2X2 className="w-4 h-4" />Large</button>
            <button
              onClick={() => setView("small")}
              className={`px-3 py-2 flex items-center gap-2 text-xs tracking-[0.15em] uppercase ${view === "small" ? "bg-[#1F3864] text-white" : "text-gray-700 hover:bg-gray-50"}`}
              data-testid="view-small"
              aria-label="Small tiles"
            ><Grid3X3 className="w-4 h-4" />Small</button>
          </div>
          <button
            onClick={downloadAll}
            disabled={photos.length === 0}
            className="btn-primary-red disabled:opacity-60"
            data-testid="download-all-zip"
          >
            <Download className="w-4 h-4" /> Download All ({photos.length}) as ZIP
          </button>
        </div>
      </section>

      {/* Grid */}
      <section className="py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {photos.length === 0 ? (
            <div className="py-20 text-center text-gray-500">No photos yet. Check back soon.</div>
          ) : (
            <div className={view === "large"
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              : "grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2"
            } data-testid="photo-grid">
              {paged.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => openLightbox(p, i)}
                  className="group relative aspect-square overflow-hidden bg-gray-100"
                  data-testid={`photo-tile-${p.id}`}
                >
                  <img
                    src={resolveUrl(p.url)}
                    alt={p.caption || event?.title || "Event photo"}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#1F3864]/0 group-hover:bg-[#1F3864]/20 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-3" data-testid="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-gray-300 disabled:opacity-40"
                data-testid="page-prev"
                aria-label="Previous page"
              ><ChevronLeft className="w-4 h-4" /></button>
              <div className="text-xs tracking-[0.2em] uppercase text-gray-600">
                Page <span className="text-[#1F3864] font-semibold">{page}</span> of {totalPages}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 border border-gray-300 disabled:opacity-40"
                data-testid="page-next"
                aria-label="Next page"
              ><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 p-2 text-white hover:text-[#C00000] transition-colors z-10"
            data-testid="lightbox-close"
            aria-label="Close"
          ><X className="w-6 h-6" /></button>
          <button
            onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
            disabled={lightbox.idx === 0}
            className="absolute left-4 p-3 text-white hover:text-[#C00000] disabled:opacity-30 z-10"
            data-testid="lightbox-prev"
            aria-label="Previous"
          ><ChevronLeft className="w-8 h-8" /></button>
          <button
            onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
            disabled={lightbox.idx === photos.length - 1}
            className="absolute right-4 p-3 text-white hover:text-[#C00000] disabled:opacity-30 z-10"
            data-testid="lightbox-next"
            aria-label="Next"
          ><ChevronRight className="w-8 h-8" /></button>
          <img
            src={resolveUrl(lightbox.url)}
            alt={lightbox.caption || "Event photo"}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[90vw] max-h-[85vh] object-contain"
          />
          <a
            href={resolveUrl(lightbox.url)}
            download
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 btn-primary-red"
            data-testid="lightbox-download"
          ><Download className="w-4 h-4" /> Download this photo</a>
        </div>
      )}
    </div>
  );
}
